import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Film, LogOut, Sparkles, Coins, Activity, CreditCard, User, Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PLAN_STYLE = {
  free: "border-white/20 text-white/60",
  pro: "border-[#00E5FF]/50 text-[#00E5FF]",
  studio: "border-[#FF007F]/50 text-[#FF007F]",
  owner: "border-[#FFD54A]/60 text-[#FFD54A]",
};

export const TopNav = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const owner = !!(user && user.is_owner);
  const staff = !!(user && user.is_staff);
  const plan = owner ? "owner" : ((user && user.plan) || "free");
  const needsVerify = !!(user && user.email_verified === false && !staff);
  const [resending, setResending] = useState(false);
  const resendVerify = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { origin: window.location.origin });
      toast.success("Verification email sent — check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend right now.");
    } finally { setResending(false); }
  };
  return (
    <>
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="nav-brand">
          <div className="w-8 h-8 rounded-md bg-[#00E5FF] flex items-center justify-center glow-cyan">
            <Film className="w-5 h-5 text-black" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">VIRAL3D</span>
        </Link>
        <div className="flex items-center gap-3">
          {user && user !== null && (
            <>
              <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold uppercase tracking-wide ${PLAN_STYLE[plan] || PLAN_STYLE.free}`} data-testid="nav-plan-badge">{plan}</span>
              <span className="hidden sm:inline-flex items-center gap-1 text-xs text-white/60" data-testid="nav-credits"><Coins className="w-3.5 h-3.5 text-[#39FF14]" />{owner ? "Unlimited" : (user.credits ?? 0)}</span>
              {owner && (
                <Button variant="outline" size="sm" onClick={() => navigate("/admin/usage")} data-testid="nav-usage-btn"
                  className="h-8 rounded-full border-[#FFD54A]/40 bg-transparent hover:bg-[#FFD54A]/10 text-[#FFD54A] text-xs">
                  <Activity className="w-3.5 h-3.5 mr-1" />Usage
                </Button>
              )}
              {!owner && (
                <Button variant="outline" size="sm" onClick={() => navigate("/pricing")} data-testid="nav-upgrade-btn"
                  className="h-8 rounded-full border-[#00E5FF]/40 bg-transparent hover:bg-[#00E5FF]/10 text-[#00E5FF] text-xs">
                  <Sparkles className="w-3.5 h-3.5 mr-1" />{plan === "studio" ? "Buy credits" : "Upgrade"}
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 text-sm text-white/60 hover:text-white outline-none" data-testid="nav-account-link">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} referrerPolicy="no-referrer"
                        className="w-8 h-8 rounded-full object-cover border border-white/20" data-testid="nav-avatar" />
                    ) : (
                      <span className="w-8 h-8 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/30 flex items-center justify-center text-[#00E5FF] text-xs font-semibold" data-testid="nav-avatar-fallback">
                        {(user.name || "?").charAt(0).toUpperCase()}
                      </span>
                    )}
                    <span className="hidden md:inline">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-zinc-950 border-white/10 text-white" data-testid="nav-account-menu">
                  <DropdownMenuLabel className="text-white/40 font-normal text-xs truncate">{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={() => navigate("/account")} className="cursor-pointer focus:bg-white/5" data-testid="menu-account">
                    <User className="w-4 h-4 mr-2 text-white/60" />Account settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(owner ? "/account" : "/pricing")} className="cursor-pointer focus:bg-white/5" data-testid="menu-billing">
                    <CreditCard className="w-4 h-4 mr-2 text-white/60" />Billing &amp; plans
                  </DropdownMenuItem>
                  {owner && (
                    <DropdownMenuItem onClick={() => navigate("/admin/usage")} className="cursor-pointer focus:bg-white/5" data-testid="menu-usage">
                      <Activity className="w-4 h-4 mr-2 text-[#FFD54A]" />Usage dashboard
                    </DropdownMenuItem>
                  )}
                  {staff && (
                    <DropdownMenuItem onClick={() => navigate("/admin/credits")} className="cursor-pointer focus:bg-white/5" data-testid="menu-admin-credits">
                      <Coins className="w-4 h-4 mr-2 text-[#39FF14]" />Credit management
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} className="cursor-pointer focus:bg-white/5 text-red-400 focus:text-red-400" data-testid="nav-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>
    </header>
    {needsVerify && (
      <div className="bg-[#FFD54A]/10 border-b border-[#FFD54A]/25 text-[#FFD54A]" data-testid="verify-email-banner">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between gap-3 text-sm">
          <span className="flex items-center gap-2"><Mail className="w-4 h-4 shrink-0" />Please verify your email to unlock rendering and generation.</span>
          <button onClick={resendVerify} disabled={resending} className="underline hover:no-underline shrink-0 flex items-center gap-1" data-testid="banner-resend-verify">
            {resending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}Resend email
          </button>
        </div>
      </div>
    )}
    </>
  );
};
